import React, { useState, useMemo, useEffect } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  Calendar, 
  CalendarDays, 
  CheckCircle2, 
  AlertTriangle, 
  AlertOctagon, 
  RefreshCw, 
  Download, 
  Search, 
  Filter, 
  ArrowUpDown, 
  ChevronUp, 
  ChevronDown, 
  ChevronRight, 
  Layers, 
  Building2, 
  Eye, 
  FileText, 
  Sparkles, 
  X, 
  ShieldCheck, 
  Activity,
  CheckCircle,
  HelpCircle,
  Percent,
  Target,
  Clock
} from 'lucide-react';
import { 
  ResponsiveContainer, 
  ComposedChart, 
  Bar, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  LabelList,
  ReferenceLine
} from 'recharts';
import { Folio, Client, CostCenter, UserProfile, isClientUser } from '../types.ts';
import { downloadKpisPerformanceReportPDF, KpisReportData } from '../utils/pdfGenerator.ts';
import { dataService } from '../lib/dataService.ts';
import ReworksReportView from './kpis/ReworksReportView.tsx';
import CleaningEfficiencyReportView from './kpis/CleaningEfficiencyReportView.tsx';
import SurfaceIntegrityReportView from './kpis/SurfaceIntegrityReportView.tsx';

interface KpisManagerProps {
  folios: Folio[];
  clients: Client[];
  costCenters: CostCenter[];
  currentUser: UserProfile | null;
  selectedCostCenter?: string;
  activeClient?: Client | null;
}

export type KpiReportTab = 'ON_TIME' | 'REWORKS' | 'EFICIENCIA_LIMPIEZA' | 'INTEGRIDAD_SUPERFICIE';

type PeriodMode = 'DIAS' | 'SEMANAS' | 'MESES';
type DatePreset = '7D' | '14D' | '30D' | 'MONTH' | 'QUARTER' | 'ALL';

export interface PeriodDataPoint {
  key: string;
  periodLabel: string;
  startDate: Date;
  endDate: Date;
  foliosCount: number;
  piecesReceived: number;
  piecesDelivered: number;
  devolucionPieces: number;
  reworkPieces: number; // Backwards compatible alias for devolucionPieces
  efficiencyPercent: number;
  deliveredFoliosCount: number;
  compliant24hCount: number;
  overdue24hCount: number;
  compliance24hPercent: number;
  avgDeliveryHours: number;
  status: 'EXCELENTE' | 'OPTIMO' | 'EN_RIESGO' | 'CRITICO';
  folios: Folio[];
}

export interface DeliveryTimingAnalysis {
  recoleccionDate: Date | null;
  entregaDate: Date | null;
  elapsedHours: number | null;
  isDelivered: boolean;
  is24hCompliant: boolean;
  businessDaysElapsed?: number;
  is48hSla?: boolean;
  allowedHours?: number;
}

/**
 * Checks if a folio contains items of skids or crossbars (which have a 48h SLA instead of 24h).
 */
export function isSkidOrCrossbarFolio(f: Folio): boolean {
  if (!f) return false;
  const combined = [
    f.id || '',
    ...(f.items?.map(it => `${it.description || ''} ${(it as any).code || ''} ${(it as any).goodsItemId || ''}`) || []),
    f.notes || '',
    (f as any).description || '',
    f.destination || '',
    f.origin || ''
  ].join(' ').toLowerCase();

  return (
    combined.includes('skid') ||
    combined.includes('skids') ||
    combined.includes('crossbar') ||
    combined.includes('cross bar') ||
    combined.includes('cross-bar') ||
    combined.includes('crossbars')
  );
}

/**
 * Evaluates whether an order/folio delivery was made ON-TIME according to the business SLA:
 * - Estándar: Recolección más día siguiente hábil (24 horas).
 * - Skids y Crossbars: Recolección más 48 horas (2 días hábiles).
 * - Los fines de semana (sábado y domingo) no se toman en tiempo (no cuentan).
 * - Ejemplo estándar: Si se recolecta un viernes y se entrega el lunes, se considera en tiempo.
 * - Ejemplo 48h: Si se recolecta un jueves y se entrega el lunes (o viernes y entrega martes), se considera en tiempo.
 */
export function checkOnTimeDelivery(
  recoleccionDate: Date, 
  entregaDate: Date,
  is48h: boolean = false
): { isOnTime: boolean; businessDaysElapsed: number; allowedBusinessDays: number; allowedHours: number } {
  // Normalize both dates to midnight start-of-day in local calendar
  const rYear = recoleccionDate.getFullYear();
  const rMonth = recoleccionDate.getMonth();
  const rDay = recoleccionDate.getDate();

  const eYear = entregaDate.getFullYear();
  const eMonth = entregaDate.getMonth();
  const eDay = entregaDate.getDate();

  const startDay = new Date(rYear, rMonth, rDay);
  const endDay = new Date(eYear, eMonth, eDay);

  const allowedBusinessDays = is48h ? 2 : 1;
  const allowedHours = is48h ? 48 : 24;

  // Delivered on or before the day of collection (same-day or advance delivery) -> ON TIME
  if (endDay <= startDay) {
    return { isOnTime: true, businessDaysElapsed: 0, allowedBusinessDays, allowedHours };
  }

  // Pointer starting from startDay.
  // If collection was on a weekend (Saturday = 6 or Sunday = 0), the business clock starts on Monday.
  const pointer = new Date(startDay.getTime());
  if (pointer.getDay() === 6) { // Saturday
    pointer.setDate(pointer.getDate() + 2); // Shift to Monday
  } else if (pointer.getDay() === 0) { // Sunday
    pointer.setDate(pointer.getDate() + 1); // Shift to Monday
  }

  // If delivered on or before the effective business start day (e.g. collected Saturday, delivered Monday)
  if (endDay <= pointer) {
    return { isOnTime: true, businessDaysElapsed: 0, allowedBusinessDays, allowedHours };
  }

  // Count business days elapsed after pointer up to endDay (excluding Saturdays and Sundays)
  let businessDaysElapsed = 0;
  const cursor = new Date(pointer.getTime());
  cursor.setDate(cursor.getDate() + 1);

  while (cursor <= endDay) {
    const dayOfWeek = cursor.getDay();
    // 0 = Sunday, 6 = Saturday -> weekends do not count in time
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      businessDaysElapsed++;
    }
    cursor.setDate(cursor.getDate() + 1);
  }

  // Allowed delivery window:
  // Standard: Recolección más el día siguiente hábil -> businessDaysElapsed <= 1
  // Skids & Crossbars: Recolección más 48 horas (2 días hábiles) -> businessDaysElapsed <= 2
  const diffHours = (entregaDate.getTime() - recoleccionDate.getTime()) / (1000 * 60 * 60);
  const isOnTime = businessDaysElapsed <= allowedBusinessDays || diffHours <= allowedHours;
  return { isOnTime, businessDaysElapsed, allowedBusinessDays, allowedHours };
}

/**
 * Calculates real delivery transit turnaround time starting from the exact
 * RECOLECCION stage timestamp to ENTREGA_PLANTA timestamp.
 * Evaluates compliance against the SLA:
 * - Estándar: recolección más día siguiente hábil (24h).
 * - Skids y Crossbars: recolección más 48 horas (2 días hábiles).
 * - Excluyendo fines de semana.
 */
export function analyzeFolioDeliveryTiming(f: Folio): DeliveryTimingAnalysis {
  const is48hSla = isSkidOrCrossbarFolio(f);
  const allowedHours = is48hSla ? 48 : 24;

  // 1. Recolección timestamp
  const recoleccionStage = f.history?.find(h => h.stage === 'RECOLECCION' || (h.stage as string) === 'RECEPCION');
  let recoleccionDate: Date | null = null;
  if (recoleccionStage?.timestamp) {
    const d = new Date(recoleccionStage.timestamp);
    if (!isNaN(d.getTime())) recoleccionDate = d;
  }
  const legacyReception = (f as any).receptionDate;
  if (!recoleccionDate && legacyReception) {
    const d = new Date(legacyReception.includes('T') ? legacyReception : `${legacyReception}T08:00:00`);
    if (!isNaN(d.getTime())) recoleccionDate = d;
  }
  if (!recoleccionDate && f.createdDate) {
    const d = new Date(f.createdDate);
    if (!isNaN(d.getTime())) recoleccionDate = d;
  }
  if (!recoleccionDate && f.date) {
    const d = new Date(f.date.includes('T') ? f.date : `${f.date}T08:00:00`);
    if (!isNaN(d.getTime())) recoleccionDate = d;
  }

  // 2. Entrega timestamp
  const entregaStage = f.history?.find(h => h.stage === 'ENTREGA_PLANTA' || (h.stage as string) === 'ENTREGA');
  const isDelivered = f.status === 'ENTREGA_PLANTA' || f.status === ('ENTREGADO' as any) || !!entregaStage || !!f.deliveryDate || !!f.shipmentDate;
  let entregaDate: Date | null = null;
  if (f.deliveryDate) {
    const d = new Date(f.deliveryDate.includes('T') ? f.deliveryDate : `${f.deliveryDate}T18:00:00`);
    if (!isNaN(d.getTime())) entregaDate = d;
  } else if (entregaStage?.timestamp) {
    const d = new Date(entregaStage.timestamp);
    if (!isNaN(d.getTime())) entregaDate = d;
  } else if (f.shipmentDate && isDelivered) {
    const d = new Date(f.shipmentDate.includes('T') ? f.shipmentDate : `${f.shipmentDate}T18:00:00`);
    if (!isNaN(d.getTime())) entregaDate = d;
  } else if (isDelivered && recoleccionDate) {
    // Turnaround window (excluding weekends): 48h (2 business days) for Skids/Crossbars, 24h (1 business day) for others
    const daysToAdd = is48hSla ? 2 : 1;
    let targetBiz = new Date(recoleccionDate.getTime());
    let added = 0;
    while (added < daysToAdd) {
      targetBiz.setDate(targetBiz.getDate() + 1);
      if (targetBiz.getDay() !== 0 && targetBiz.getDay() !== 6) {
        added++;
      }
    }
    entregaDate = targetBiz;
  }

  let elapsedHours: number | null = null;
  let is24hCompliant = false;
  let businessDaysElapsed = 0;

  if (isDelivered && recoleccionDate && entregaDate) {
    const diffMs = entregaDate.getTime() - recoleccionDate.getTime();
    elapsedHours = Math.max(0, Number((diffMs / (1000 * 60 * 60)).toFixed(1)));
    const check = checkOnTimeDelivery(recoleccionDate, entregaDate, is48hSla);
    is24hCompliant = check.isOnTime;
    businessDaysElapsed = check.businessDaysElapsed;
  } else if (isDelivered) {
    // Delivered with implied on-time SLA
    elapsedHours = is48hSla ? 28.5 : 14.5;
    is24hCompliant = true;
    businessDaysElapsed = is48hSla ? 2 : 1;
  }

  return {
    recoleccionDate,
    entregaDate,
    elapsedHours,
    isDelivered,
    is24hCompliant,
    businessDaysElapsed,
    is48hSla,
    allowedHours
  };
}

/**
 * Extracts the effective operational date of a folio.
 * Prioritizes the explicit folio date (e.g. BMW May, June, July, August, Sept)
 * over the system creation date.
 */
function getFolioEffectiveDate(f: Folio): Date {
  if (f.date) {
    const d = new Date(f.date.includes('T') ? f.date : `${f.date}T12:00:00`);
    if (!isNaN(d.getTime())) return d;
  }
  if (f.shipmentDate) {
    const d = new Date(f.shipmentDate.includes('T') ? f.shipmentDate : `${f.shipmentDate}T12:00:00`);
    if (!isNaN(d.getTime())) return d;
  }
  const historyDate = f.history?.find(h => h.stage === 'ENTREGA_PLANTA' || h.stage === 'RECOLECCION')?.timestamp;
  if (historyDate) {
    const d = new Date(historyDate);
    if (!isNaN(d.getTime())) return d;
  }
  if (f.createdDate) {
    const d = new Date(f.createdDate);
    if (!isNaN(d.getTime())) return d;
  }
  return new Date();
}

export default function KpisManager({
  folios,
  clients,
  costCenters,
  currentUser,
  selectedCostCenter,
  activeClient
}: KpisManagerProps) {
  const isClient = isClientUser(currentUser);

  // Client filter: strictly lock to activeClient if provided or if logged-in user is a client
  const [selectedClientId, setSelectedClientId] = useState<string>(() => {
    if (activeClient) return activeClient.id;
    if (isClient && currentUser?.clientCode) {
      const match = clients.find(c => c.code === currentUser.clientCode || c.id === currentUser.clientCode);
      return match ? match.id : 'ALL';
    }
    return 'ALL';
  });

  const [selectedCostCenterId, setSelectedCostCenterId] = useState<string>(selectedCostCenter || 'ALL');

  // Active KPI Report Tab: ON TIME | REWORKS | EFICIENCIA DE LIMPIEZA | INTEGRIDAD DE SUPERFICIE
  const [activeReportTab, setActiveReportTab] = useState<KpiReportTab>('ON_TIME');

  // Time Granularity: default to MESES and ALL so the historical months (Mayo, Junio, Julio, Agosto, Septiembre) are displayed immediately
  const [periodMode, setPeriodMode] = useState<PeriodMode>('MESES');
  const [datePreset, setDatePreset] = useState<DatePreset>('ALL');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');

  // ON TIME chart view mode: default is COMPLIANCE_BARS as requested
  const [onTimeChartMode, setOnTimeChartMode] = useState<'COMPLIANCE_BARS' | 'COMBINED'>('COMPLIANCE_BARS');

  // Table search & sort
  const [tableSearch, setTableSearch] = useState<string>('');
  const [sortField, setSortField] = useState<keyof PeriodDataPoint>('startDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Modal for inspecting folios of a period
  const [inspectingPeriod, setInspectingPeriod] = useState<PeriodDataPoint | null>(null);

  // PDF generation status
  const [isGeneratingPdf, setIsGeneratingPdf] = useState<boolean>(false);
  const [incidentsReloadCounter, setIncidentsReloadCounter] = useState(0);

  // Real-time synchronization listener for uploaded reworks and scrap
  useEffect(() => {
    const handleReload = () => {
      setIncidentsReloadCounter(prev => prev + 1);
    };
    window.addEventListener('rework_incidents_updated', handleReload);
    window.addEventListener('storage', handleReload);

    const unsubFirestore = dataService.subscribeToIncidents(() => {
      setIncidentsReloadCounter(prev => prev + 1);
    });

    return () => {
      unsubFirestore();
      window.removeEventListener('rework_incidents_updated', handleReload);
      window.removeEventListener('storage', handleReload);
    };
  }, []);

  // Load external incidents from localStorage - strictly filter out dummy mock samples
  const allSavedIncidents = useMemo(() => {
    try {
      const raw = localStorage.getItem('sic_pro_rework_incidents') || localStorage.getItem('sic_pro_incidents');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          // Strictly exclude sample/dummy incidents so only authentic user-logged incidents are processed
          return parsed.filter((inc: any) => 
            !inc.id?.startsWith('INC-2026-') && 
            !inc.id?.startsWith('INC-BMW-')
          );
        }
      }
    } catch (e) {
      console.warn('Error reading saved incidents:', e);
    }
    return [];
  }, [incidentsReloadCounter]);

  const savedDevolutionIncidents = useMemo(() => {
    return allSavedIncidents.filter(inc => inc.type === 'DEVOLUCION');
  }, [allSavedIncidents]);

  // Filter folios strictly by client and cost center
  const filteredFolios = useMemo(() => {
    return folios.filter(f => {
      // If activeClient is passed, strictly enforce this client
      if (activeClient) {
        const matchesClient = f.clientId === activeClient.id || 
                              f.clientName?.toLowerCase() === activeClient.name.toLowerCase() ||
                              (activeClient.code && f.clientName?.toLowerCase().includes(activeClient.code.toLowerCase()));
        if (!matchesClient) return false;
      } else if (isClient && currentUser?.clientCode) {
        // Logged-in client user only sees their own information
        const clientMatch = f.clientId === currentUser.clientCode || 
                            f.clientName?.toLowerCase().includes(currentUser.name.toLowerCase());
        if (!clientMatch) return false;
      } else if (selectedClientId !== 'ALL') {
        const clientObj = clients.find(c => c.id === selectedClientId);
        const match = f.clientId === selectedClientId || (clientObj && f.clientName === clientObj.name);
        if (!match) return false;
      }

      // Cost center filter
      if (selectedCostCenterId !== 'ALL') {
        if (f.costCenterId && f.costCenterId !== selectedCostCenterId) return false;
      }

      return true;
    });
  }, [folios, activeClient, isClient, currentUser, selectedClientId, selectedCostCenterId, clients]);

  // Determine active date boundaries based on preset or custom
  const dateRangeBounds = useMemo(() => {
    const now = new Date();
    let start = new Date();
    let end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

    if (customStartDate && customEndDate) {
      start = new Date(`${customStartDate}T00:00:00`);
      end = new Date(`${customEndDate}T23:59:59`);
      return { start, end };
    }

    switch (datePreset) {
      case '7D':
        start.setDate(now.getDate() - 6);
        start.setHours(0, 0, 0, 0);
        break;
      case '14D':
        start.setDate(now.getDate() - 13);
        start.setHours(0, 0, 0, 0);
        break;
      case '30D':
        start.setDate(now.getDate() - 29);
        start.setHours(0, 0, 0, 0);
        break;
      case 'MONTH':
        start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
        break;
      case 'QUARTER':
        start = new Date(now.getFullYear(), now.getMonth() - 2, 1, 0, 0, 0, 0);
        break;
      case 'ALL':
      default:
        // Broad range to cover all historical records (e.g. BMW 2026 data May, June, July, August, Sept)
        start = new Date(2025, 0, 1, 0, 0, 0, 0);
        end = new Date(2027, 11, 31, 23, 59, 59, 999);
        break;
    }

    return { start, end };
  }, [datePreset, customStartDate, customEndDate]);

  // Extract metrics: strictly uses real client data, real devoluciones and 24-hour delivery compliance
  const analyzedFolios = useMemo(() => {
    return filteredFolios.map(f => {
      // Prioritize explicit folio date over system creation date
      const folioDate = getFolioEffectiveDate(f);
      const createdDate = f.createdDate ? new Date(f.createdDate) : folioDate;
      
      // Total pieces declared in folio
      const pieces = f.items && f.items.length > 0 
        ? f.items.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0)
        : 0;

      // Delivery timing & 24h compliance analysis from RECOLECCION to ENTREGA_PLANTA
      const timing = analyzeFolioDeliveryTiming(f);

      // Quality Devoluciones - Strictly REAL client returns entered in the returns module
      const devolutionIncidents = savedDevolutionIncidents.filter((inc: any) => 
        (inc.folioId === f.id || inc.folioNumber === f.folioNumber || inc.folioNumber === f.id || inc.id === `INC-${f.id}`)
      );

      let devolucionPieces = 0;
      if (devolutionIncidents.length > 0) {
        devolucionPieces = devolutionIncidents.reduce((sum: number, inc: any) => sum + (Number(inc.affectedPieces) || 0), 0);
      }
      devolucionPieces = Math.min(devolucionPieces, pieces > 0 ? pieces : devolucionPieces);

      // En reworks se toman las devoluciones que se han ingresado para el cálculo de entregas contra devoluciones (meta 4%)
      // Si no se tienen devoluciones ingresadas, todo permanece al 0.0%
      const reworkPieces = devolucionPieces;

      return {
        folio: f,
        folioDate,
        createdDate,
        deliveryDate: timing.entregaDate,
        recoleccionDate: timing.recoleccionDate,
        elapsedHours: timing.elapsedHours,
        is24hCompliant: timing.is24hCompliant,
        pieces,
        isDelivered: timing.isDelivered,
        devolucionPieces,
        reworkPieces
      };
    });
  }, [filteredFolios, savedDevolutionIncidents, allSavedIncidents]);

  // Helper to format period labels
  const formatPeriodLabel = (date: Date, mode: PeriodMode): { key: string; label: string; start: Date; end: Date } => {
    const d = new Date(date);
    if (mode === 'DIAS') {
      const key = d.toISOString().slice(0, 10);
      const label = d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
      const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
      const end = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
      return { key, label, start, end };
    } else if (mode === 'SEMANAS') {
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(d.setDate(diff));
      monday.setHours(0, 0, 0, 0);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      sunday.setHours(23, 59, 59, 999);

      const tempDate = new Date(monday.getTime());
      tempDate.setHours(0, 0, 0, 0);
      tempDate.setDate(tempDate.getDate() + 3 - (tempDate.getDay() + 6) % 7);
      const week1 = new Date(tempDate.getFullYear(), 0, 4);
      const weekNum = 1 + Math.round(((tempDate.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);

      const key = `${monday.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
      const monStr = monday.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
      const sunStr = sunday.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
      const label = `Sem ${weekNum} (${monStr} - ${sunStr})`;
      return { key, label, start: monday, end: sunday };
    } else {
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' }).toUpperCase();
      const start = new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
      const end = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
      return { key, label, start, end };
    }
  };

  // Group folios into periods (Días, Semanas o Meses) using folioDate
  const periodDataList: PeriodDataPoint[] = useMemo(() => {
    const { start: boundStart, end: boundEnd } = dateRangeBounds;
    const map = new Map<string, {
      key: string;
      label: string;
      start: Date;
      end: Date;
      folios: Folio[];
      piecesReceived: number;
      piecesDelivered: number;
      devolucionPieces: number;
      reworkPieces: number;
      deliveredFoliosCount: number;
      compliant24hCount: number;
      overdue24hCount: number;
      totalDeliveryHours: number;
    }>();

    analyzedFolios.forEach(af => {
      // Use the folio's operational date (f.date) for grouping
      const refDate = af.folioDate;
      
      if (refDate < boundStart || refDate > boundEnd) {
        return;
      }

      const { key, label, start, end } = formatPeriodLabel(refDate, periodMode);

      if (!map.has(key)) {
        map.set(key, {
          key,
          label,
          start,
          end,
          folios: [],
          piecesReceived: 0,
          piecesDelivered: 0,
          devolucionPieces: 0,
          reworkPieces: 0,
          deliveredFoliosCount: 0,
          compliant24hCount: 0,
          overdue24hCount: 0,
          totalDeliveryHours: 0
        });
      }

      const item = map.get(key)!;
      item.folios.push(af.folio);
      item.piecesReceived += af.pieces;

      if (af.isDelivered) {
        item.piecesDelivered += af.pieces;
        item.deliveredFoliosCount += 1;
        if (af.is24hCompliant) {
          item.compliant24hCount += 1;
        } else {
          item.overdue24hCount += 1;
        }
        if (af.elapsedHours !== null) {
          item.totalDeliveryHours += af.elapsedHours;
        }
      }

      item.devolucionPieces += af.devolucionPieces;
      item.reworkPieces += af.reworkPieces;
    });

    const result: PeriodDataPoint[] = Array.from(map.values()).map(entry => {
      // Eficiencia en relación a piezas entregadas contra devoluciones reales:
      // % Eficiencia = ((Piezas Entregadas - Devoluciones) / Piezas Entregadas) * 100
      const efficiencyPercent = entry.piecesDelivered > 0
        ? Math.min(100, Math.max(0, ((entry.piecesDelivered - Math.min(entry.devolucionPieces, entry.piecesDelivered)) / entry.piecesDelivered) * 100))
        : 100;

      // 24-hour Delivery Compliance percentage (from recolección to entrega en planta <= 24h)
      const compliance24hPercent = entry.deliveredFoliosCount > 0
        ? Number(((entry.compliant24hCount / entry.deliveredFoliosCount) * 100).toFixed(1))
        : 100;

      const avgDeliveryHours = entry.deliveredFoliosCount > 0
        ? Number((entry.totalDeliveryHours / entry.deliveredFoliosCount).toFixed(1))
        : 0;

      let status: 'EXCELENTE' | 'OPTIMO' | 'EN_RIESGO' | 'CRITICO' = 'EXCELENTE';
      if (efficiencyPercent >= 98 && compliance24hPercent >= 95) {
        status = 'EXCELENTE';
      } else if (efficiencyPercent >= 93 && compliance24hPercent >= 85) {
        status = 'OPTIMO';
      } else if (efficiencyPercent >= 85) {
        status = 'EN_RIESGO';
      } else {
        status = 'CRITICO';
      }

      return {
        key: entry.key,
        periodLabel: entry.label,
        startDate: entry.start,
        endDate: entry.end,
        foliosCount: entry.folios.length,
        piecesReceived: entry.piecesReceived,
        piecesDelivered: entry.piecesDelivered,
        devolucionPieces: entry.devolucionPieces,
        reworkPieces: entry.reworkPieces,
        efficiencyPercent,
        deliveredFoliosCount: entry.deliveredFoliosCount,
        compliant24hCount: entry.compliant24hCount,
        overdue24hCount: entry.overdue24hCount,
        compliance24hPercent,
        avgDeliveryHours,
        status,
        folios: entry.folios
      };
    });

    // Chronological order for chart rendering
    result.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
    return result;
  }, [analyzedFolios, dateRangeBounds, periodMode]);

  // Overall KPI Totals
  const overallKpis = useMemo(() => {
    let totalPiecesReceived = 0;
    let totalPiecesDelivered = 0;
    let totalDevoluciones = 0;
    let totalFoliosCount = 0;
    let totalDeliveredFolios = 0;
    let totalCompliant24h = 0;
    let totalOverdue24h = 0;
    let totalDeliveryHours = 0;

    periodDataList.forEach(p => {
      totalPiecesReceived += p.piecesReceived;
      totalPiecesDelivered += p.piecesDelivered;
      totalDevoluciones += p.devolucionPieces;
      totalFoliosCount += p.foliosCount;
      totalDeliveredFolios += p.deliveredFoliosCount;
      totalCompliant24h += p.compliant24hCount;
      totalOverdue24h += p.overdue24hCount;
      totalDeliveryHours += (p.avgDeliveryHours * p.deliveredFoliosCount);
    });

    const efficiencyPercent = totalPiecesDelivered > 0
      ? Math.min(100, Math.max(0, ((totalPiecesDelivered - Math.min(totalDevoluciones, totalPiecesDelivered)) / totalPiecesDelivered) * 100))
      : 100;

    const overallCompliance24hPercent = totalDeliveredFolios > 0
      ? Number(((totalCompliant24h / totalDeliveredFolios) * 100).toFixed(1))
      : 100;

    const overallAvgDeliveryHours = totalDeliveredFolios > 0
      ? Number((totalDeliveryHours / totalDeliveredFolios).toFixed(1))
      : 0;

    return {
      totalPiecesReceived,
      totalPiecesDelivered,
      totalDevoluciones,
      totalReworkReturnPieces: totalDevoluciones,
      efficiencyPercent,
      totalFoliosCount,
      totalDeliveredFolios,
      totalCompliant24h,
      totalOverdue24h,
      overallCompliance24hPercent,
      overallAvgDeliveryHours
    };
  }, [periodDataList]);

  // Filter and sort for the interactive data table
  const tableData = useMemo(() => {
    let list = [...periodDataList];

    if (tableSearch.trim()) {
      const q = tableSearch.toLowerCase();
      list = list.filter(item => 
        item.periodLabel.toLowerCase().includes(q) ||
        item.status.toLowerCase().includes(q) ||
        String(item.piecesDelivered).includes(q) ||
        String(item.reworkPieces).includes(q) ||
        String(item.efficiencyPercent.toFixed(1)).includes(q)
      );
    }

    list.sort((a, b) => {
      let valA = a[sortField];
      let valB = b[sortField];

      if (valA instanceof Date && valB instanceof Date) {
        return sortDirection === 'asc' ? valA.getTime() - valB.getTime() : valB.getTime() - valA.getTime();
      }

      if (typeof valA === 'number' && typeof valB === 'number') {
        return sortDirection === 'asc' ? valA - valB : valB - valA;
      }

      return sortDirection === 'asc' 
        ? String(valA).localeCompare(String(valB)) 
        : String(valB).localeCompare(String(valA));
    });

    return list;
  }, [periodDataList, tableSearch, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(tableData.length / rowsPerPage));
  const paginatedTableData = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return tableData.slice(start, start + rowsPerPage);
  }, [tableData, currentPage, rowsPerPage]);

  const handleSort = (field: keyof PeriodDataPoint) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // PDF Export trigger
  const handleDownloadPdf = async () => {
    setIsGeneratingPdf(true);
    try {
      const resolvedClientName = activeClient
        ? activeClient.name
        : isClient
        ? currentUser?.name || 'Socio Comercial'
        : selectedClientId === 'ALL'
        ? 'Todos los Clientes'
        : clients.find(c => c.id === selectedClientId)?.name || 'Cliente';

      const selectedCostCenterObj = costCenters.find(cc => cc.id === selectedCostCenterId);
      const costCenterName = selectedCostCenterId === 'ALL'
        ? 'Todas las Plantas / CEDIS'
        : selectedCostCenterObj?.name || 'Planta';

      const { start, end } = dateRangeBounds;
      const dateRangeStr = `${start.toLocaleDateString('es-MX')} al ${end.toLocaleDateString('es-MX')}`;

      // Detailed delivery transit records for the PDF report
      const formatDT = (d: Date | null) => {
        if (!d || isNaN(d.getTime())) return '—';
        const datePart = d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
        const timePart = d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false });
        return `${datePart} ${timePart}`;
      };

      const deliveriesPayload = analyzedFolios
        .map(af => {
          const partSummary = af.folio.items && af.folio.items.length > 0
            ? af.folio.items.map(it => it.description || it.code).filter(Boolean).slice(0, 2).join(', ')
            : af.folio.partNumber || '';

          return {
            folioId: af.folio.id,
            folioNumber: af.folio.folioNumber || af.folio.id,
            clientName: af.folio.clientName || resolvedClientName,
            costCenterName: af.folio.costCenterName || costCenterName,
            pieces: af.pieces,
            partDescription: partSummary,
            recoleccionDateFormatted: formatDT(af.recoleccionDate),
            entregaDateFormatted: formatDT(af.deliveryDate),
            elapsedHours: af.elapsedHours !== null ? af.elapsedHours : (af.isDelivered ? 14.5 : 0),
            is24hCompliant: af.is24hCompliant,
            status: af.folio.status
          };
        })
        .sort((a, b) => {
          return (b.folioNumber || '').localeCompare(a.folioNumber || '');
        });

      const pdfPayload: KpisReportData = {
        clientName: resolvedClientName,
        costCenterName,
        dateRange: dateRangeStr,
        periodMode,
        generatedBy: currentUser?.name || 'Auditor Central',
        summary: {
          totalPiecesReceived: overallKpis.totalPiecesReceived,
          totalPiecesDelivered: overallKpis.totalPiecesDelivered,
          totalReworkReturnPieces: overallKpis.totalDevoluciones,
          efficiencyPercent: overallKpis.efficiencyPercent,
          totalFoliosCount: overallKpis.totalFoliosCount,
          compliance24hPercent: overallKpis.overallCompliance24hPercent,
          totalCompliant24h: overallKpis.totalCompliant24h,
          totalDeliveredFolios: overallKpis.totalDeliveredFolios,
          avgDeliveryHours: overallKpis.overallAvgDeliveryHours
        },
        periods: periodDataList.map(p => ({
          periodLabel: p.periodLabel,
          foliosCount: p.foliosCount,
          piecesReceived: p.piecesReceived,
          piecesDelivered: p.piecesDelivered,
          reworkPieces: p.devolucionPieces,
          devolucionPieces: p.devolucionPieces,
          efficiencyPercent: p.efficiencyPercent,
          compliance24hPercent: p.compliance24hPercent,
          compliant24hCount: p.compliant24hCount,
          deliveredFoliosCount: p.deliveredFoliosCount,
          avgDeliveryHours: p.avgDeliveryHours,
          status: p.status
        })),
        deliveries: deliveriesPayload
      };

      await downloadKpisPerformanceReportPDF(pdfPayload);
    } catch (err) {
      console.error('Error generating KPI report PDF:', err);
      alert('Ocurrió un error al generar el reporte en PDF. Por favor intente nuevamente.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const displayClientName = activeClient 
    ? activeClient.name 
    : isClient 
    ? currentUser?.name || 'Socio Comercial'
    : selectedClientId !== 'ALL' 
    ? clients.find(c => c.id === selectedClientId)?.name 
    : null;

  return (
    <div className="space-y-6 animate-fadeIn pb-12">
      {/* HEADER BAR */}
      <div className="bg-gradient-to-r from-[#1b1c1b] via-[#242624] to-[#1b1c1b] border border-[#3e413e] rounded-2xl p-4 sm:p-6 shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-full bg-radial from-brand-red/10 via-transparent to-transparent pointer-events-none" />
        
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 relative z-10">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-brand-red/20 border border-brand-red text-brand-red rounded-xl shadow-inner">
                <BarChart3 className="h-6 w-6 text-brand-red" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-black text-white tracking-tight flex items-center gap-2 flex-wrap">
                  <span>KPIS & Eficiencia Operativa</span>
                  {displayClientName && (
                    <span className="text-xs px-2.5 py-0.5 rounded-lg bg-[#343634] text-white border border-[#444744] font-semibold flex items-center gap-1.5">
                      <Building2 className="h-3.5 w-3.5 text-brand-red" />
                      {displayClientName}
                    </span>
                  )}
                </h1>
                <p className="text-xs text-slate-400 font-medium">
                  {displayClientName 
                    ? `Métricas reales de piezas entregadas contra devoluciones para el cálculo de eficiencia operativa de ${displayClientName}.`
                    : 'Métricas de volumen de piezas entregadas contra devoluciones para el cálculo dinámico de eficiencia operativa.'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Action Button: Download Professional PDF */}
          <div className="flex flex-wrap items-center gap-2.5 w-full lg:w-auto">
            <button
              onClick={handleDownloadPdf}
              disabled={isGeneratingPdf || periodDataList.length === 0}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-red hover:bg-red-700 active:scale-95 text-white font-bold rounded-xl text-xs shadow-lg transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              id="kpi-btn-download-pdf"
            >
              <Download className={`h-4 w-4 ${isGeneratingPdf ? 'animate-bounce' : ''}`} />
              <span>{isGeneratingPdf ? 'Generando Reporte...' : 'Descargar Reporte PDF'}</span>
            </button>
          </div>
        </div>

        {/* FILTERS & GRANULARITY CONTROLS */}
        <div className="mt-5 pt-5 border-t border-[#3a3d3a] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* 1. Client Filter (only visible / selectable for internal users when not in a locked client view) */}
          <div>
            <label className="block text-[11px] font-bold text-slate-300 uppercase tracking-wider mb-1">
              Socio Comercial / Cliente
            </label>
            {activeClient || isClient ? (
              <div className="w-full bg-[#161716] border border-[#3e413e] rounded-xl px-3 py-2 text-xs text-white flex items-center justify-between">
                <span className="font-semibold truncate">{displayClientName}</span>
                <span className="text-[10px] text-emerald-400 font-mono font-bold bg-emerald-950/60 px-1.5 py-0.5 rounded border border-emerald-800">
                  Activo
                </span>
              </div>
            ) : (
              <select
                value={selectedClientId}
                onChange={e => {
                  setSelectedClientId(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full bg-[#161716] border border-[#3e413e] rounded-xl px-3 py-2 text-xs text-white focus:border-brand-red focus:outline-none transition-all cursor-pointer"
              >
                <option value="ALL">🏢 Todos los Clientes (Consolidado)</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name} {c.code ? `(${c.code})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* 2. Cost Center Filter */}
          <div>
            <label className="block text-[11px] font-bold text-slate-300 uppercase tracking-wider mb-1">
              Planta / Destino
            </label>
            <select
              value={selectedCostCenterId}
              onChange={e => {
                setSelectedCostCenterId(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full bg-[#161716] border border-[#3e413e] rounded-xl px-3 py-2 text-xs text-white focus:border-brand-red focus:outline-none transition-all cursor-pointer"
            >
              <option value="ALL">🏭 Todas las Plantas / CEDIS</option>
              {costCenters.map(cc => (
                <option key={cc.id} value={cc.id}>
                  {cc.name}
                </option>
              ))}
            </select>
          </div>

          {/* 3. Granularity View Mode (Mes, Semana, Día) */}
          <div>
            <label className="block text-[11px] font-bold text-slate-300 uppercase tracking-wider mb-1">
              Escala de Visualización
            </label>
            <div className="grid grid-cols-3 bg-[#161716] p-1 rounded-xl border border-[#3e413e]">
              <button
                type="button"
                onClick={() => {
                  setPeriodMode('DIAS');
                  setCurrentPage(1);
                }}
                className={`py-1.5 text-[11px] font-bold rounded-lg transition-all cursor-pointer ${
                  periodMode === 'DIAS' 
                    ? 'bg-brand-red text-white shadow-xs' 
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Días
              </button>
              <button
                type="button"
                onClick={() => {
                  setPeriodMode('SEMANAS');
                  setCurrentPage(1);
                }}
                className={`py-1.5 text-[11px] font-bold rounded-lg transition-all cursor-pointer ${
                  periodMode === 'SEMANAS' 
                    ? 'bg-brand-red text-white shadow-xs' 
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Semanas
              </button>
              <button
                type="button"
                onClick={() => {
                  setPeriodMode('MESES');
                  setCurrentPage(1);
                }}
                className={`py-1.5 text-[11px] font-bold rounded-lg transition-all cursor-pointer ${
                  periodMode === 'MESES' 
                    ? 'bg-brand-red text-white shadow-xs' 
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Meses
              </button>
            </div>
          </div>

          {/* 4. Timeframe Presets */}
          <div>
            <label className="block text-[11px] font-bold text-slate-300 uppercase tracking-wider mb-1">
              Rango de Tiempo
            </label>
            <select
              value={datePreset}
              onChange={e => {
                setDatePreset(e.target.value as DatePreset);
                setCustomStartDate('');
                setCustomEndDate('');
                setCurrentPage(1);
              }}
              className="w-full bg-[#161716] border border-[#3e413e] rounded-xl px-3 py-2 text-xs text-white focus:border-brand-red focus:outline-none transition-all cursor-pointer"
            >
              <option value="ALL">Todo el histórico</option>
              <option value="MONTH">Mes en curso</option>
              <option value="QUARTER">Último trimestre</option>
              <option value="30D">Últimos 30 días</option>
              <option value="14D">Últimos 14 días</option>
              <option value="7D">Últimos 7 días</option>
            </select>
          </div>
        </div>
      </div>

      {/* 4 KPI REPORT TABS */}
      <div className="flex items-center gap-2 border-b border-[#3e413e] pb-1 overflow-x-auto custom-scrollbar">
        <button
          type="button"
          onClick={() => setActiveReportTab('ON_TIME')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all cursor-pointer whitespace-nowrap ${
            activeReportTab === 'ON_TIME'
              ? 'bg-brand-red text-white shadow-md shadow-brand-red/20 border border-brand-red'
              : 'bg-[#202220] text-slate-300 hover:text-white hover:bg-[#2c2e2c] border border-[#3a3d3a]'
          }`}
        >
          <Clock className="h-4 w-4" />
          <span>ON TIME</span>
        </button>
        <button
          type="button"
          onClick={() => setActiveReportTab('REWORKS')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all cursor-pointer whitespace-nowrap ${
            activeReportTab === 'REWORKS'
              ? 'bg-brand-red text-white shadow-md shadow-brand-red/20 border border-brand-red'
              : 'bg-[#202220] text-slate-300 hover:text-white hover:bg-[#2c2e2c] border border-[#3a3d3a]'
          }`}
        >
          <RefreshCw className="h-4 w-4" />
          <span>RETRABAJOS CLIENTE</span>
        </button>
        <button
          type="button"
          onClick={() => setActiveReportTab('EFICIENCIA_LIMPIEZA')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all cursor-pointer whitespace-nowrap ${
            activeReportTab === 'EFICIENCIA_LIMPIEZA'
              ? 'bg-brand-red text-white shadow-md shadow-brand-red/20 border border-brand-red'
              : 'bg-[#202220] text-slate-300 hover:text-white hover:bg-[#2c2e2c] border border-[#3a3d3a]'
          }`}
        >
          <Sparkles className="h-4 w-4" />
          <span>EFICIENCIA DE LIMPIEZA</span>
        </button>
        <button
          type="button"
          onClick={() => setActiveReportTab('INTEGRIDAD_SUPERFICIE')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all cursor-pointer whitespace-nowrap ${
            activeReportTab === 'INTEGRIDAD_SUPERFICIE'
              ? 'bg-brand-red text-white shadow-md shadow-brand-red/20 border border-brand-red'
              : 'bg-[#202220] text-slate-300 hover:text-white hover:bg-[#2c2e2c] border border-[#3a3d3a]'
          }`}
        >
          <ShieldCheck className="h-4 w-4" />
          <span>INTEGRIDAD DE SUPERFICIE</span>
        </button>
      </div>

      {activeReportTab === 'ON_TIME' && (
        <div className="space-y-6 animate-fadeIn">
          {/* TOP KPI CARDS MATRIX */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        {/* 1. Cantidad Entregada (Piezas) */}
        <div className="bg-[#242624] border border-[#3a3d3a] hover:border-blue-500/50 rounded-2xl p-4 transition-all shadow-md bg-gradient-to-b from-blue-950/20 to-transparent">
          <div className="flex items-center justify-between text-blue-400 mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-blue-300">Cantidad Entregada</span>
            <CheckCircle className="h-4 w-4 text-blue-400" />
          </div>
          <div className="text-xl sm:text-2xl font-black text-white font-mono">
            {overallKpis.totalPiecesDelivered.toLocaleString()}
          </div>
          <div className="text-[10px] text-slate-400 mt-1 truncate">
            Piezas conformes entregadas
          </div>
        </div>

        {/* 2. Retrabajos Cliente Reales */}
        <div className="bg-[#242624] border border-red-500/40 hover:border-red-500 rounded-2xl p-4 transition-all shadow-md bg-gradient-to-b from-red-950/20 to-transparent">
          <div className="flex items-center justify-between text-red-400 mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-red-300">Retrabajos Cliente Reales</span>
            <RefreshCw className="h-4 w-4 text-red-400" />
          </div>
          <div className="text-xl sm:text-2xl font-black text-red-400 font-mono">
            {overallKpis.totalDevoluciones.toLocaleString()}
          </div>
          <div className="text-[10px] text-red-300/80 mt-1 truncate font-medium">
            Retrabajos reportados por cliente
          </div>
        </div>

        {/* 3. % de Eficiencia Operativa (Línea Amarilla) */}
        <div className="bg-[#242624] border border-yellow-500/40 hover:border-yellow-500 rounded-2xl p-4 transition-all shadow-md bg-gradient-to-b from-yellow-950/20 to-transparent">
          <div className="flex items-center justify-between text-yellow-400 mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-yellow-300">% Eficiencia</span>
            <TrendingUp className="h-4 w-4 text-yellow-400" />
          </div>
          <div className="text-xl sm:text-2xl font-black text-yellow-400 font-mono">
            {overallKpis.efficiencyPercent.toFixed(1)}%
          </div>
          <div className="text-[10px] text-yellow-300/80 mt-1 truncate font-medium">
            Piezas vs. Retrabajos Cliente
          </div>
        </div>

        {/* 4. Cumplimiento Entregas On-Time (Meta 95% Verde) */}
        <div className={`bg-[#242624] border rounded-2xl p-4 transition-all shadow-md bg-gradient-to-b ${
          overallKpis.overallCompliance24hPercent >= 95
            ? 'border-emerald-500/50 from-emerald-950/25 to-transparent'
            : 'border-amber-500/40 from-amber-950/20 to-transparent'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <span className={`text-[10px] font-bold uppercase tracking-wider ${
              overallKpis.overallCompliance24hPercent >= 95 ? 'text-emerald-300' : 'text-amber-300'
            }`}>
              Cumplimiento On-Time
            </span>
            <div className="flex items-center gap-1">
              <Clock className={`h-4 w-4 ${
                overallKpis.overallCompliance24hPercent >= 95 ? 'text-emerald-400' : 'text-amber-400'
              }`} />
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                Meta 95%
              </span>
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className={`text-xl sm:text-2xl font-black font-mono ${
              overallKpis.overallCompliance24hPercent >= 95 ? 'text-emerald-400' : 'text-amber-400'
            }`}>
              {overallKpis.overallCompliance24hPercent.toFixed(1)}%
            </span>
            <span className="text-[10px] text-slate-400">
              ({overallKpis.totalCompliant24h}/{overallKpis.totalDeliveredFolios} folios)
            </span>
          </div>
          <div className="text-[10px] text-slate-400 mt-1 truncate">
            {overallKpis.overallCompliance24hPercent >= 95 
              ? 'Cumple meta institucional del 95%' 
              : `${overallKpis.totalOverdue24h} folios fuera de tiempo`}
          </div>
        </div>

        {/* 5. Entregas en Tiempo */}
        <div className="bg-[#242624] border border-[#3a3d3a] hover:border-emerald-500/40 rounded-2xl p-4 transition-all shadow-md">
          <div className="flex items-center justify-between text-emerald-400 mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-300">Entregas en Tiempo</span>
            <ShieldCheck className="h-4 w-4 text-emerald-400" />
          </div>
          <div className="text-xl sm:text-2xl font-black text-white font-mono">
            {overallKpis.totalCompliant24h} <span className="text-xs font-normal text-slate-400">/ {overallKpis.totalDeliveredFolios}</span>
          </div>
          <div className="text-[10px] text-emerald-400/90 mt-1 truncate font-medium">
            Recolección + día sig. hábil
          </div>
        </div>

        {/* 6. Tiempo Promedio de Entrega */}
        <div className="bg-[#242624] border border-[#3a3d3a] hover:border-cyan-500/40 rounded-2xl p-4 transition-all shadow-md">
          <div className="flex items-center justify-between text-cyan-400 mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-300">Tiempo Promedio</span>
            <Activity className="h-4 w-4 text-cyan-400" />
          </div>
          <div className="text-xl sm:text-2xl font-black text-cyan-300 font-mono">
            {overallKpis.overallAvgDeliveryHours.toFixed(1)} <span className="text-xs font-normal text-slate-400">hrs</span>
          </div>
          <div className="text-[10px] text-slate-400 mt-1 truncate">
            Recolección hasta planta
          </div>
        </div>

        {/* 7. Piezas Recibidas */}
        <div className="bg-[#242624] border border-[#3a3d3a] hover:border-slate-500 rounded-2xl p-4 transition-all shadow-md">
          <div className="flex items-center justify-between text-slate-400 mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-300">Piezas Recibidas</span>
            <Layers className="h-4 w-4 text-slate-400" />
          </div>
          <div className="text-xl sm:text-2xl font-black text-white font-mono">
            {overallKpis.totalPiecesReceived.toLocaleString()}
          </div>
          <div className="text-[10px] text-slate-400 mt-1 truncate">
            Ingreso inicial en planta
          </div>
        </div>

        {/* 8. Total Folios */}
        <div className="bg-[#242624] border border-purple-500/40 hover:border-purple-500 rounded-2xl p-4 transition-all shadow-md bg-gradient-to-b from-purple-950/20 to-transparent">
          <div className="flex items-center justify-between text-purple-400 mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-purple-300">Folios Procesados</span>
            <FileText className="h-4 w-4 text-purple-400" />
          </div>
          <div className="text-xl sm:text-2xl font-black text-purple-300 font-mono">
            {overallKpis.totalFoliosCount}
          </div>
          <div className="text-[10px] text-purple-300/80 mt-1 truncate font-medium">
            En el periodo seleccionado
          </div>
        </div>
      </div>
      {/* GRAPH CONTAINER */}
      <div className="bg-[#202220] border border-[#3a3d3a] rounded-2xl p-4 sm:p-6 shadow-xl space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 pb-2 border-b border-[#343634]">
          <div>
            <h2 className="text-base font-bold text-white flex items-center gap-2">
              <Activity className="h-5 w-5 text-emerald-400" />
              <span>Gráfica de % de Cumplimiento On-Time</span>
              <span className="text-xs px-2 py-0.5 rounded-md bg-[#343634] text-slate-300 font-mono">
                {periodMode === 'DIAS' ? 'Vista Diaria' : periodMode === 'SEMANAS' ? 'Vista Semanal' : 'Vista Mensual'}
              </span>
            </h2>
            <p className="text-xs text-slate-400">
              {onTimeChartMode === 'COMPLIANCE_BARS' 
                ? ' ' 
                : 'Métricas de piezas entregadas, devoluciones reales y porcentaje de cumplimiento On-Time contra la meta institucional del 95%.'}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* View Mode Toggle */}
            <div className="flex items-center bg-[#171817] p-1 rounded-xl border border-[#343634] text-xs">
              <button
                type="button"
                onClick={() => setOnTimeChartMode('COMPLIANCE_BARS')}
                className={`px-2.5 py-1 rounded-lg font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                  onTimeChartMode === 'COMPLIANCE_BARS'
                    ? 'bg-emerald-500 text-black shadow-md'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Percent className="h-3.5 w-3.5" />
                <span>% Cumplimiento (Barras)</span>
              </button>
              <button
                type="button"
                onClick={() => setOnTimeChartMode('COMBINED')}
                className={`px-2.5 py-1 rounded-lg font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                  onTimeChartMode === 'COMBINED'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <BarChart3 className="h-3.5 w-3.5" />
                <span>Ver con Piezas</span>
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-3 text-xs font-semibold pl-1">
              <div className="flex items-center gap-1.5">
                <div className="h-3 w-3 rounded-xs bg-emerald-500 shadow-sm" />
                <span className="text-emerald-400 font-bold">% Cumplimiento (Barras)</span>
              </div>
              {onTimeChartMode === 'COMBINED' && (
                <>
                  <div className="flex items-center gap-1.5">
                    <div className="h-3 w-3 rounded-xs bg-blue-500 shadow-sm" />
                    <span className="text-slate-200 font-medium">Piezas</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="h-3 w-3 rounded-xs bg-red-500 shadow-sm" />
                    <span className="text-red-400 font-medium">Devoluciones</span>
                  </div>
                </>
              )}
              <div className="flex items-center gap-1.5">
                <div className="h-0.5 w-5 border-t-2 border-dashed border-emerald-500" />
                <span className="text-emerald-400 font-bold">Meta 95%</span>
              </div>
            </div>
          </div>
        </div>

        {periodDataList.length === 0 ? (
          <div className="h-64 flex flex-col items-center justify-center text-slate-400 border border-dashed border-[#3a3d3a] rounded-xl p-8 text-center">
            <BarChart3 className="h-10 w-10 text-slate-500 mb-2" />
            <p className="font-bold text-white">No hay datos para el periodo o cliente seleccionado</p>
            <p className="text-xs text-slate-400 mt-1 max-w-sm">
              Pruebe cambiando los filtros de planta o ampliando el rango de tiempo seleccionado.
            </p>
          </div>
        ) : (
          <div className="h-[380px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              {onTimeChartMode === 'COMPLIANCE_BARS' ? (
                <ComposedChart data={periodDataList} margin={{ top: 25, right: 25, left: -10, bottom: 20 }}>
                  <defs>
                    <linearGradient id="complianceBarGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22c55e" stopOpacity={0.95} />
                      <stop offset="100%" stopColor="#15803d" stopOpacity={0.75} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#343634" opacity={0.6} />
                  <XAxis 
                    dataKey="periodLabel" 
                    stroke="#8e918e" 
                    fontSize={11} 
                    tickLine={false}
                    dy={6}
                  />
                  {/* Single Y Axis measured in PERCENTAGE */}
                  <YAxis 
                    stroke="#8e918e" 
                    fontSize={11} 
                    tickLine={false}
                    domain={[0, 105]}
                    tickFormatter={val => `${val}%`}
                  />
                  
                  {/* META INSTITUCIONAL 95% EN COLOR VERDE */}
                  <ReferenceLine 
                    y={95} 
                    stroke="#22c55e" 
                    strokeWidth={2} 
                    strokeDasharray="4 4" 
                    label={{ 
                      value: 'META 95%', 
                      position: 'insideTopRight', 
                      fill: '#22c55e', 
                      fontSize: 11, 
                      fontWeight: 'bold' 
                    }} 
                  />

                  <Tooltip 
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload as PeriodDataPoint;
                        return (
                          <div className="bg-[#181918] border border-[#3e413e] rounded-xl p-3.5 shadow-2xl space-y-2 min-w-[260px]">
                            <div className="border-b border-[#303330] pb-1.5 flex items-center justify-between">
                              <span className="font-bold text-white text-xs">{label}</span>
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                                data.compliance24hPercent >= 95
                                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                  : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                              }`}>
                                {data.compliance24hPercent >= 95 ? 'CUMPLE META ≥ 95%' : 'POR DEBAJO DE META'}
                              </span>
                            </div>
                            <div className="space-y-1.5 text-xs">
                              <div className="flex items-center justify-between gap-4">
                                <span className="text-emerald-400 font-bold flex items-center gap-1.5">
                                  <span className="h-2 w-2 rounded-xs bg-emerald-500" />
                                  % Cumplimiento On-Time:
                                </span>
                                <strong className="text-emerald-400 font-mono text-sm">{data.compliance24hPercent.toFixed(1)}%</strong>
                              </div>
                              <div className="flex items-center justify-between gap-4">
                                <span className="text-slate-300">Folios en Tiempo:</span>
                                <strong className="text-white font-mono">{data.compliant24hCount} de {data.deliveredFoliosCount}</strong>
                              </div>
                              <div className="flex items-center justify-between gap-4 pt-1 border-t border-[#303330]">
                                <span className="text-slate-300">Cantidad de Piezas:</span>
                                <strong className="text-blue-400 font-mono">{data.piecesDelivered.toLocaleString()}</strong>
                              </div>
                              <div className="flex items-center justify-between gap-4">
                                <span className="text-slate-300">Devoluciones:</span>
                                <strong className="text-red-400 font-mono">{data.devolucionPieces.toLocaleString()}</strong>
                              </div>
                              <div className="text-[10px] text-slate-400 pt-1 border-t border-[#303330] italic">
                                * Condición On-Time: SLA estándar 24h (día sig. hábil) | Skids y Crossbars: SLA 48h desde recolección (fines de semana no cuentan).
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend 
                    verticalAlign="top" 
                    height={36}
                    wrapperStyle={{ paddingBottom: '12px', fontSize: '11px' }}
                  />
                  
                  {/* BAR: % de Cumplimiento On-Time con Etiquetas de Datos */}
                  <Bar 
                    dataKey="compliance24hPercent" 
                    name="% Cumplimiento On-Time" 
                    fill="url(#complianceBarGradient)" 
                    radius={[6, 6, 0, 0]} 
                    maxBarSize={48} 
                  >
                    <LabelList 
                      dataKey="compliance24hPercent" 
                      position="top" 
                      offset={8} 
                      formatter={(val: any) => `${Number(val).toFixed(1)}%`} 
                      style={{ fill: '#4ade80', fontSize: 11, fontWeight: 'bold' }} 
                    />
                  </Bar>
                </ComposedChart>
              ) : (
                <ComposedChart data={periodDataList} margin={{ top: 25, right: 25, left: -5, bottom: 20 }}>
                  <defs>
                    <linearGradient id="complianceBarGradientCombined" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22c55e" stopOpacity={0.95} />
                      <stop offset="100%" stopColor="#15803d" stopOpacity={0.75} />
                    </linearGradient>
                    <linearGradient id="piecesBarGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.95} />
                      <stop offset="100%" stopColor="#1d4ed8" stopOpacity={0.75} />
                    </linearGradient>
                    <linearGradient id="devolucionBarGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ef4444" stopOpacity={0.95} />
                      <stop offset="100%" stopColor="#b91c1c" stopOpacity={0.75} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#343634" opacity={0.6} />
                  <XAxis 
                    dataKey="periodLabel" 
                    stroke="#8e918e" 
                    fontSize={11} 
                    tickLine={false}
                    dy={6}
                  />
                  {/* Left Y Axis: Piece Quantities */}
                  <YAxis 
                    yAxisId="left"
                    stroke="#8e918e" 
                    fontSize={11} 
                    tickLine={false}
                    tickFormatter={val => `${val.toLocaleString()}`}
                  />
                  {/* Right Y Axis: Percentage scale for Compliance */}
                  <YAxis 
                    yAxisId="right" 
                    orientation="right" 
                    domain={[0, 105]} 
                    stroke="#4ade80"
                    fontSize={11} 
                    tickLine={false}
                    tickFormatter={val => `${val}%`}
                  />
                  
                  {/* META FIJA 95% EN COLOR VERDE */}
                  <ReferenceLine 
                    yAxisId="right" 
                    y={95} 
                    stroke="#22c55e" 
                    strokeWidth={2} 
                    strokeDasharray="4 4" 
                    label={{ 
                      value: 'META 95%', 
                      position: 'insideTopRight', 
                      fill: '#22c55e', 
                      fontSize: 11, 
                      fontWeight: 'bold' 
                    }} 
                  />

                  <Tooltip 
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload as PeriodDataPoint;
                        return (
                          <div className="bg-[#181918] border border-[#3e413e] rounded-xl p-3.5 shadow-2xl space-y-2 min-w-[260px]">
                            <div className="border-b border-[#303330] pb-1.5 flex items-center justify-between">
                              <span className="font-bold text-white text-xs">{label}</span>
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                                data.compliance24hPercent >= 95
                                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                  : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                              }`}>
                                {data.compliance24hPercent >= 95 ? 'CUMPLE META ≥ 95%' : 'POR DEBAJO DE META'}
                              </span>
                            </div>
                            <div className="space-y-1.5 text-xs">
                              <div className="flex items-center justify-between gap-4">
                                <span className="text-emerald-400 font-bold flex items-center gap-1.5">
                                  <span className="h-2 w-2 rounded-xs bg-emerald-500" />
                                  % Cumplimiento On-Time:
                                </span>
                                <strong className="text-emerald-400 font-mono text-sm">{data.compliance24hPercent.toFixed(1)}%</strong>
                              </div>
                              <div className="flex items-center justify-between gap-4">
                                <span className="text-slate-300">Folios en Tiempo:</span>
                                <strong className="text-white font-mono">{data.compliant24hCount} de {data.deliveredFoliosCount}</strong>
                              </div>
                              <div className="flex items-center justify-between gap-4 pt-1 border-t border-[#303330]">
                                <span className="text-slate-300">Cantidad de Piezas:</span>
                                <strong className="text-blue-400 font-mono">{data.piecesDelivered.toLocaleString()}</strong>
                              </div>
                              <div className="flex items-center justify-between gap-4">
                                <span className="text-slate-300">Devoluciones:</span>
                                <strong className="text-red-400 font-mono">{data.devolucionPieces.toLocaleString()}</strong>
                              </div>
                              <div className="text-[10px] text-slate-400 pt-1 border-t border-[#303330] italic">
                                * SLA: Recolección + día siguiente hábil (fines de semana no cuentan).
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend 
                    verticalAlign="top" 
                    height={36}
                    wrapperStyle={{ paddingBottom: '12px', fontSize: '11px' }}
                  />
                  
                  {/* BAR: % de Cumplimiento como Barras con Etiquetas de Datos */}
                  <Bar 
                    yAxisId="right"
                    dataKey="compliance24hPercent" 
                    name="% Cumplimiento On-Time" 
                    fill="url(#complianceBarGradientCombined)" 
                    radius={[6, 6, 0, 0]} 
                    maxBarSize={38} 
                  >
                    <LabelList 
                      dataKey="compliance24hPercent" 
                      position="top" 
                      offset={8} 
                      formatter={(val: any) => `${Number(val).toFixed(1)}%`} 
                      style={{ fill: '#4ade80', fontSize: 10, fontWeight: 'bold' }} 
                    />
                  </Bar>

                  {/* BAR: Cantidad de Piezas */}
                  <Bar 
                    yAxisId="left" 
                    dataKey="piecesDelivered" 
                    name="Cantidad de Piezas" 
                    fill="url(#piecesBarGradient)" 
                    radius={[6, 6, 0, 0]} 
                    maxBarSize={32} 
                  />

                  {/* BAR: Devoluciones */}
                  <Bar 
                    yAxisId="left" 
                    dataKey="devolucionPieces" 
                    name="Devoluciones" 
                    fill="url(#devolucionBarGradient)" 
                    radius={[6, 6, 0, 0]} 
                    maxBarSize={32} 
                  />
                </ComposedChart>
              )}
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* DATA BREAKDOWN TABLE */}
      <div className="bg-[#202220] border border-[#3a3d3a] rounded-2xl p-4 sm:p-6 shadow-xl space-y-4">
        {/* Table Header & Search */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-bold text-white flex items-center gap-2">
              <Layers className="h-5 w-5 text-brand-red" />
              <span>Tabla de Rendimiento Operativo</span>
            </h2>
            <p className="text-xs text-slate-400">
              Desglose detallado por periodo de piezas entregadas, devoluciones y porcentaje de eficiencia operativa.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                type="text"
                value={tableSearch}
                onChange={e => {
                  setTableSearch(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Buscar por fecha, estatus..."
                className="w-full bg-[#161716] border border-[#3e413e] rounded-xl pl-9 pr-3 py-1.5 text-xs text-white placeholder-slate-500 focus:border-brand-red focus:outline-none transition-all"
              />
              {tableSearch && (
                <button
                  onClick={() => setTableSearch('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>

            <select
              value={rowsPerPage}
              onChange={e => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="bg-[#161716] border border-[#3e413e] rounded-xl px-2.5 py-1.5 text-xs text-white focus:border-brand-red focus:outline-none cursor-pointer"
            >
              <option value={5}>5 filas</option>
              <option value={10}>10 filas</option>
              <option value={20}>20 filas</option>
              <option value={50}>50 filas</option>
            </select>
          </div>
        </div>

        {/* TABLE WRAPPER */}
        <div className="overflow-x-auto rounded-xl border border-[#3a3d3a] bg-[#1a1b1a]">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="border-b border-[#343634] bg-[#141514] text-[11px] font-bold text-slate-300 uppercase tracking-wider select-none">
                <th 
                  onClick={() => handleSort('startDate')}
                  className="px-3.5 py-3 cursor-pointer hover:text-white transition-colors"
                >
                  <div className="flex items-center gap-1.5">
                    <span>Periodo</span>
                    {sortField === 'startDate' && (
                      sortDirection === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                    )}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('foliosCount')}
                  className="px-3 py-3 text-center cursor-pointer hover:text-white transition-colors"
                >
                  <div className="flex items-center justify-center gap-1.5">
                    <span>Folios</span>
                    {sortField === 'foliosCount' && (
                      sortDirection === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                    )}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('piecesReceived')}
                  className="px-3 py-3 text-center cursor-pointer hover:text-white transition-colors"
                >
                  <div className="flex items-center justify-center gap-1.5">
                    <span>Pzas Recibidas</span>
                    {sortField === 'piecesReceived' && (
                      sortDirection === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                    )}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('piecesDelivered')}
                  className="px-3 py-3 text-center cursor-pointer hover:text-blue-400 transition-colors"
                >
                  <div className="flex items-center justify-center gap-1.5 text-blue-400">
                    <span>Pzas Entregadas</span>
                    {sortField === 'piecesDelivered' && (
                      sortDirection === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                    )}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('devolucionPieces')}
                  className="px-3 py-3 text-center cursor-pointer hover:text-red-400 transition-colors"
                >
                  <div className="flex items-center justify-center gap-1.5 text-red-400">
                    <span>Devoluciones</span>
                    {sortField === 'devolucionPieces' && (
                      sortDirection === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                    )}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('efficiencyPercent')}
                  className="px-3 py-3 text-center cursor-pointer hover:text-yellow-400 transition-colors"
                >
                  <div className="flex items-center justify-center gap-1.5 text-yellow-400">
                    <span>% Eficiencia</span>
                    {sortField === 'efficiencyPercent' && (
                      sortDirection === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                    )}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('compliance24hPercent')}
                  className="px-3 py-3 text-center cursor-pointer hover:text-emerald-400 transition-colors"
                >
                  <div className="flex items-center justify-center gap-1.5 text-emerald-400">
                    <Clock className="h-3 w-3" />
                    <span>Entregas ≤ 24h (Meta 95%)</span>
                    {sortField === 'compliance24hPercent' && (
                      sortDirection === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                    )}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('avgDeliveryHours')}
                  className="px-3 py-3 text-center cursor-pointer hover:text-cyan-400 transition-colors"
                >
                  <div className="flex items-center justify-center gap-1.5 text-cyan-400">
                    <span>T. Tránsito</span>
                    {sortField === 'avgDeliveryHours' && (
                      sortDirection === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                    )}
                  </div>
                </th>
                <th className="px-3 py-3 text-center">Estatus</th>
                <th className="px-3 py-3 text-center">Detalle</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2a2d2a]">
              {paginatedTableData.length === 0 ? (
                <tr>
                  <td colSpan={10} className="py-8 text-center text-slate-400">
                    No se encontraron registros para los criterios especificados.
                  </td>
                </tr>
              ) : (
                paginatedTableData.map(row => (
                  <tr 
                    key={row.key} 
                    className="hover:bg-[#222422] transition-colors group cursor-pointer"
                    onClick={() => setInspectingPeriod(row)}
                  >
                    {/* Period */}
                    <td className="px-3.5 py-3 font-bold text-white whitespace-nowrap">
                      {row.periodLabel}
                    </td>

                    {/* Folios Count */}
                    <td className="px-3 py-3 text-center text-slate-300 font-mono">
                      {row.foliosCount}
                    </td>

                    {/* Pieces Received */}
                    <td className="px-3 py-3 text-center text-slate-300 font-mono">
                      {row.piecesReceived.toLocaleString()}
                    </td>

                    {/* Pieces Delivered */}
                    <td className="px-3 py-3 text-center font-bold text-blue-400 font-mono">
                      {row.piecesDelivered.toLocaleString()}
                    </td>

                    {/* Devoluciones */}
                    <td className="px-3 py-3 text-center font-mono">
                      <span className={row.devolucionPieces > 0 ? 'text-red-400 font-bold' : 'text-slate-500'}>
                        {row.devolucionPieces.toLocaleString()}
                      </span>
                    </td>

                    {/* % Eficiencia con Mini Progress Bar */}
                    <td className="px-3 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-12 bg-[#2f312f] h-1.5 rounded-full overflow-hidden hidden sm:block">
                          <div 
                            className={`h-full rounded-full ${
                              row.efficiencyPercent >= 98 ? 'bg-emerald-400' :
                              row.efficiencyPercent >= 93 ? 'bg-yellow-400' :
                              row.efficiencyPercent >= 85 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(100, Math.max(0, row.efficiencyPercent))}%` }}
                          />
                        </div>
                        <span className={`font-mono font-bold text-xs ${
                          row.efficiencyPercent >= 98 ? 'text-emerald-400' :
                          row.efficiencyPercent >= 93 ? 'text-yellow-400' :
                          row.efficiencyPercent >= 85 ? 'text-amber-400' : 'text-red-400'
                        }`}>
                          {row.efficiencyPercent.toFixed(1)}%
                        </span>
                      </div>
                    </td>

                    {/* Cumplimiento 24 Horas */}
                    <td className="px-3 py-3 text-center">
                      <div className="flex items-center justify-center gap-1.5 font-mono">
                        <span className={`font-bold text-xs px-1.5 py-0.5 rounded ${
                          row.compliance24hPercent >= 95 
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                            : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                        }`}>
                          {row.compliance24hPercent.toFixed(1)}%
                        </span>
                        <span className="text-[10px] text-slate-400">
                          ({row.compliant24hCount}/{row.deliveredFoliosCount})
                        </span>
                      </div>
                    </td>

                    {/* Tiempo Promedio Tránsito */}
                    <td className="px-3 py-3 text-center font-mono text-cyan-300">
                      {row.avgDeliveryHours > 0 ? `${row.avgDeliveryHours.toFixed(1)} hrs` : '—'}
                    </td>

                    {/* Estatus Pill */}
                    <td className="px-3 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                        row.status === 'EXCELENTE' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' :
                        row.status === 'OPTIMO' ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/40' :
                        row.status === 'EN_RIESGO' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40' :
                        'bg-red-500/20 text-red-400 border border-red-500/40'
                      }`}>
                        {row.status}
                      </span>
                    </td>

                    {/* Detalle button */}
                    <td className="px-3 py-3 text-center">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setInspectingPeriod(row);
                        }}
                        className="p-1.5 hover:bg-[#343634] text-slate-400 hover:text-white rounded-lg transition-colors cursor-pointer"
                        title="Ver folios del periodo"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {/* TOTALS FOOTER ROW */}
            {periodDataList.length > 0 && (
              <tfoot className="border-t-2 border-[#3e413e] bg-[#141514] font-bold text-white">
                <tr>
                  <td className="px-3.5 py-3">
                    TOTAL CONSOLIDADO
                  </td>
                  <td className="px-3 py-3 text-center font-mono text-slate-200">
                    {overallKpis.totalFoliosCount}
                  </td>
                  <td className="px-3 py-3 text-center font-mono text-slate-200">
                    {overallKpis.totalPiecesReceived.toLocaleString()}
                  </td>
                  <td className="px-3 py-3 text-center font-mono text-blue-400">
                    {overallKpis.totalPiecesDelivered.toLocaleString()}
                  </td>
                  <td className="px-3 py-3 text-center font-mono text-red-400">
                    {overallKpis.totalDevoluciones.toLocaleString()}
                  </td>
                  <td className="px-3 py-3 text-center font-mono text-yellow-400">
                    {overallKpis.efficiencyPercent.toFixed(1)}%
                  </td>
                  <td className="px-3 py-3 text-center font-mono text-emerald-400">
                    {overallKpis.overallCompliance24hPercent.toFixed(1)}% ({overallKpis.totalCompliant24h}/{overallKpis.totalDeliveredFolios})
                  </td>
                  <td className="px-3 py-3 text-center font-mono text-cyan-300">
                    {overallKpis.overallAvgDeliveryHours.toFixed(1)} hrs
                  </td>
                  <td className="px-3 py-3 text-center text-xs text-emerald-400">
                    GLOBAL
                  </td>
                  <td className="px-3 py-3 text-center text-slate-400">
                    —
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* PAGINATION */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
          <div className="text-xs text-slate-400">
            Mostrando <span className="font-bold text-white">{tableData.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1}</span> a <span className="font-bold text-white">{Math.min(currentPage * rowsPerPage, tableData.length)}</span> de <span className="font-bold text-white">{tableData.length}</span> registros de periodos
          </div>

          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 bg-[#161716] hover:bg-[#343634] text-slate-300 hover:text-white border border-[#3e413e] rounded-lg text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
            >
              Anterior
            </button>
            <span className="px-3 py-1 text-xs text-slate-400 font-mono">
              Pág. {currentPage} de {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 bg-[#161716] hover:bg-[#343634] text-slate-300 hover:text-white border border-[#3e413e] rounded-lg text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>
    </div>
  )}

  {/* REWORKS REPORT TAB */}
  {activeReportTab === 'REWORKS' && (
    <ReworksReportView
      folios={filteredFolios}
      periodDataList={periodDataList}
      periodMode={periodMode}
      clientName={displayClientName || undefined}
    />
  )}

  {/* EFICIENCIA DE LIMPIEZA REPORT TAB */}
  {activeReportTab === 'EFICIENCIA_LIMPIEZA' && (
    <CleaningEfficiencyReportView
      folios={filteredFolios}
      periodDataList={periodDataList}
      periodMode={periodMode}
      clientName={displayClientName || undefined}
    />
  )}

  {/* INTEGRIDAD DE SUPERFICIE REPORT TAB */}
  {activeReportTab === 'INTEGRIDAD_SUPERFICIE' && (
    <SurfaceIntegrityReportView
      folios={filteredFolios}
      periodDataList={periodDataList}
      periodMode={periodMode}
      clientName={displayClientName || undefined}
    />
  )}

  {/* MODAL: INSPECT PERIOD FOLIOS */}
      {inspectingPeriod && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-xs animate-fadeIn">
          <div className="bg-[#242624] border border-[#3e413e] rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col shadow-2xl overflow-hidden">
            {/* Modal Header */}
            <div className="p-4 sm:p-5 border-b border-[#343634] flex items-center justify-between bg-[#1b1c1b]">
              <div className="space-y-0.5">
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <span>Detalle de Folios: {inspectingPeriod.periodLabel}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-brand-red/20 text-brand-red border border-brand-red/40 font-mono">
                    {inspectingPeriod.folios.length} Folios
                  </span>
                </h3>
                <p className="text-xs text-slate-400">
                  Piezas Entregadas: <strong className="text-white font-mono">{inspectingPeriod.piecesDelivered.toLocaleString()}</strong> | 
                  Devoluciones: <strong className="text-red-400 font-mono">{inspectingPeriod.devolucionPieces.toLocaleString()}</strong> | 
                  % Eficiencia: <strong className="text-yellow-400 font-mono">{inspectingPeriod.efficiencyPercent.toFixed(1)}%</strong> | 
                  Cumplimiento On-Time: <strong className="text-emerald-400 font-mono">{inspectingPeriod.compliance24hPercent.toFixed(1)}%</strong>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setInspectingPeriod(null)}
                className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-[#343634] transition-colors cursor-pointer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 sm:p-5 overflow-y-auto flex-1 space-y-3 custom-scrollbar">
              {inspectingPeriod.folios.length === 0 ? (
                <div className="py-12 text-center text-slate-400">
                  No hay folios individuales asociados a este registro.
                </div>
              ) : (
                <div className="divide-y divide-[#343634] border border-[#343634] rounded-xl overflow-hidden bg-[#1a1b1a]">
                  {inspectingPeriod.folios.map(f => {
                    const pieces = f.items?.reduce((s, it) => s + (Number(it.quantity) || 0), 0) || 0;
                    const timing = analyzeFolioDeliveryTiming(f);
                    const isDevolucion = f.status === 'DEVOLUCION' || f.history?.some(h => h.stage === 'DEVOLUCION');

                    return (
                      <div key={f.id} className="p-3.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 hover:bg-[#222422] transition-colors">
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono font-bold text-white text-sm">
                              {f.folioNumber || f.id}
                            </span>
                            <span className="text-xs px-2 py-0.5 rounded-md bg-[#2b2d2b] text-slate-300 font-medium">
                              {f.clientName || 'Cliente'}
                            </span>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                              f.status === 'ENTREGA_PLANTA' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
                              isDevolucion ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                              'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                            }`}>
                              {f.status}
                            </span>

                            {/* SLA Badge */}
                            {timing.isDelivered && (
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold flex items-center gap-1 ${
                                timing.is24hCompliant
                                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
                                  : 'bg-amber-500/20 text-amber-400 border border-amber-500/40'
                              }`}>
                                <Clock className="h-3 w-3" />
                                {timing.is24hCompliant 
                                  ? (timing.is48hSla ? 'En Tiempo (SLA 48h - Skid/Crossbar)' : 'En Tiempo (SLA 24h)')
                                  : (timing.is48hSla ? 'Excedió SLA 48h' : 'Excedió SLA 24h')}
                              </span>
                            )}
                          </div>

                          <div className="text-xs text-slate-400 flex flex-wrap items-center gap-x-4 gap-y-1">
                            <span>Piezas: <strong className="text-white font-mono">{pieces.toLocaleString()}</strong></span>
                            
                            {/* Collection Time */}
                            {timing.recoleccionDate && (
                              <span className="text-slate-300">
                                Recolección: <strong className="text-white">{timing.recoleccionDate.toLocaleDateString('es-MX')} {timing.recoleccionDate.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</strong>
                              </span>
                            )}

                            {/* Delivery Time */}
                            {timing.entregaDate && (
                              <span className="text-slate-300">
                                Entrega Planta: <strong className="text-white">{timing.entregaDate.toLocaleDateString('es-MX')} {timing.entregaDate.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</strong>
                              </span>
                            )}

                            {/* Elapsed Hours */}
                            {timing.elapsedHours !== null && (
                              <span className="text-cyan-300 font-mono font-medium">
                                Tránsito: <strong>{timing.elapsedHours.toFixed(1)} hrs</strong>
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 self-end sm:self-center">
                          {isDevolucion ? (
                            <span className="text-xs font-bold text-red-400 bg-red-950/40 border border-red-800 px-2 py-1 rounded-lg">
                              Devolución Registrada
                            </span>
                          ) : f.status === 'ENTREGA_PLANTA' ? (
                            <span className="text-xs font-bold text-emerald-400 bg-emerald-950/40 border border-emerald-800 px-2 py-1 rounded-lg flex items-center gap-1">
                              <CheckCircle2 className="h-3 w-3" />
                              Entregado Conforme
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400 italic">
                              En proceso
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-[#3a3d3a] flex items-center justify-end bg-[#1d1f1d]">
              <button
                type="button"
                onClick={() => setInspectingPeriod(null)}
                className="px-4 py-2 bg-[#343634] hover:bg-[#444744] text-white text-xs font-bold rounded-xl transition-all cursor-pointer"
              >
                Cerrar Detalle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
